import { inject, Injectable } from '@angular/core';
import { EPermissions, IUserLogin } from '@auth/login/data/login.model';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { resetEmpAssignment } from '@pay/pricing-assign-emp-tariff/store/ngrx/emp-assignment.actions';
import { resetDirectPaymentConfigs } from '@pay/pricing-multitenant-direct-payment/store/ngrx/direct-payment-configs.actions';
import { ReceiptDownloadMonitorService } from '@pay/receipts/services/receipt-download-monitor.service';
import {
  resetPartners,
  resetValueInputLimits,
} from '@pay/pricing-shared/store/pricing-shared.actions';
import { PricingSubscriptionsActions } from '@pay/pricing-subscription/ngrx/pricing-subscriptions.actions';
import { AuthenticationService } from '@shared/services/auth/authentication.service';
import { StorageBroadcastService } from '@shared/services/auth/storage-broadcast.service';
import { SnackbarService } from '@shared/services/notification/snackbar.service';
import { TopNotificationService } from '@shared/services/notification/top-notification.service';
import { PermissionService } from '@shared/services/util/permission.service';
import Keycloak from 'keycloak-js';
import { from, of } from 'rxjs';
import { catchError, distinctUntilChanged, filter, map, switchMap, tap } from 'rxjs/operators';
import userflow from 'userflow.js';
import { MasterDataService } from '../../business-partner/master-data/services/master-data.service';
import { SessionActions } from './session.actions';
import { SessionFacade } from '@shared/store/session.facade';
import { FeatureFlagService } from '@shared/services/feature-flag/feature-flag.service';

@Injectable({
  providedIn: 'root',
})
export class SessionEffects {
  private readonly actions$ = inject(Actions);
  private readonly ngrxStore = inject(Store);
  private readonly authenticationService = inject(AuthenticationService);
  private readonly keycloak = inject(Keycloak);
  private readonly topNotificationService = inject(TopNotificationService);
  private readonly broadcastService = inject(StorageBroadcastService);
  private readonly permissionService = inject(PermissionService);
  private readonly masterDataService = inject(MasterDataService);
  private readonly snackBar = inject(SnackbarService);
  private readonly receiptDownloadMonitorService = inject(ReceiptDownloadMonitorService);
  private readonly featureFlagService = inject(FeatureFlagService);
  private readonly sessionFacade = inject(SessionFacade);

  initFeatureFlags$ = createEffect(
    () =>
      this.sessionFacade.userLogin$.pipe(
        map(user => user as IUserLogin),
        filter(user => !!user?.settings?.email),
        distinctUntilChanged(
          (previous, current) => previous.settings.email === current.settings.email
        ),
        switchMap(user => from(this.featureFlagService.initGrowthBook(user)))
      ),
    { dispatch: false }
  );

  logout$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SessionActions.logout),
      tap(() => this.receiptDownloadMonitorService.stopFetching()),
      switchMap(() => this.performLogout(true))
    )
  );

  logoutFromBroadcast$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SessionActions.logoutFromBroadcast),
      tap(() => this.receiptDownloadMonitorService.stopFetching()),
      switchMap(() => this.performLogout(false))
    )
  );

  private performLogout(shouldBroadcast: boolean) {
    // needed for closing download progress bar when opened
    this.snackBar.closeSnackbar();

    return from(this.keycloak.logout({ redirectUri: window.location.origin })).pipe(
      map(() => {
        if (shouldBroadcast) {
          this.featureFlagService.destroyGrowthBook();
          this.broadcastService.broadcastLogout();
        }
        return SessionActions.logoutSuccess();
      }),
      catchError(error => {
        this.topNotificationService.showErrorInTopNotification(error);
        return of(SessionActions.logoutError());
      })
    );
  }

  logoutSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SessionActions.logoutSuccess),
      map(() => {
        userflow.reset();
        // Only the app state is cleared. Do NOT call `localStorage.clear()` or
        // remove the GrowthBook cache key here — the last known feature payload
        // must survive logout so the next login can fall back to it when
        // GrowthBook is unreachable (see `FeatureFlagService.cacheKey`).
        this.ngrxStore.dispatch(resetPartners());
        this.ngrxStore.dispatch(resetValueInputLimits());
        this.ngrxStore.dispatch(resetEmpAssignment());
        this.ngrxStore.dispatch(resetDirectPaymentConfigs());
        this.ngrxStore.dispatch(PricingSubscriptionsActions.resetSubscriptions());
        localStorage.removeItem('state');
        return SessionActions.closeTopNotification();
      })
    )
  );

  fetchUserSettingsValueList$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SessionActions.loginSuccess),
      switchMap(() => {
        return this.authenticationService.getSettingValueList();
      }),
      map(result => SessionActions.setValueList({ settings: result })),
      catchError(error => {
        this.topNotificationService.showErrorInTopNotification(error);
        return of(SessionActions.setValueList({ settings: null }));
      })
    )
  );

  fetchLoginInfoMessages$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SessionActions.loginSuccess, SessionActions.getInfoMessages),
      switchMap(() => {
        if (this.permissionService.userHasPermission(EPermissions.VIEW_BP_MASTERDATA_POPUP)) {
          return this.masterDataService.checkMasterDataComplete();
        } else {
          return of(null);
        }
      }),
      map(infoMessage => {
        const messages = [];
        if (infoMessage) {
          messages.push(infoMessage);
        }

        return SessionActions.setInfoMessages({ messages: messages });
      }),
      catchError(error => {
        this.topNotificationService.showErrorInTopNotification(error);
        return of(SessionActions.setInfoMessages({ messages: [] }));
      })
    )
  );
}
